import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Film, 
  ArrowLeft, 
  Sparkles, 
  Download,
  ChevronRight,
  ChevronDown,
  Loader2,
  Lock,
  Crown,
  FileText,
  Star,
  Lightbulb,
  Circle,
  CheckCircle,
  PenTool,
  RotateCcw,
  Eye,
  MessageCircle,
  Zap,
  ArrowRight,
  Edit3,
  Trophy,
  Clock
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Import extracted components and constants
import { STORY_BEATS, CHOICE_STEPS, FREE_TIER_BEATS } from './ScriptEditor/constants';
import { MobileLeftSidebar, MobileRightSidebar, MobileHeader } from './ScriptEditor/MobileSidebars';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScriptEditor = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState(null);
  
  // Beat selection
  const [selectedAct, setSelectedAct] = useState(1);
  const [selectedBeat, setSelectedBeat] = useState(null);
  const [expandedActs, setExpandedActs] = useState({ 1: true, 2: false, 3: false });
  
  // Collaborative choice flow
  const [currentStep, setCurrentStep] = useState(0); // 0: plot, 1: dialogue, 2: character
  const [userChoices, setUserChoices] = useState({ plot: null, dialogue: null, character_action: null });
  const [aiOptions, setAiOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInput, setCustomInput] = useState('');
  
  // Scene generation
  const [generatingScene, setGeneratingScene] = useState(false);
  const [generatedScene, setGeneratedScene] = useState(null);
  const [viewingScene, setViewingScene] = useState(false);
  
  // Post-generation feedback
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
  const [customSuggestion, setCustomSuggestion] = useState('');
  const [rewritingScene, setRewritingScene] = useState(false);
  
  // Coverage report
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageReport, setCoverageReport] = useState(null);
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  
  // Mobile sidebar state
  const [showMobileLeftSidebar, setShowMobileLeftSidebar] = useState(false);
  const [showMobileRightSidebar, setShowMobileRightSidebar] = useState(false);
  
  // Challenge state
  const [challengeEntered, setChallengeEntered] = useState(false);
  const [enteringChallenge, setEnteringChallenge] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0 });
  
  // Daily usage limit state
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [dailyUsage, setDailyUsage] = useState({ used: 0, limit: 5, remaining: 5, unlimited: false });

  // Pro Scene Studio state
  const [showProStudio, setShowProStudio] = useState(false);
  const [proStudioInputs, setProStudioInputs] = useState({
    sceneContext: '',
    includeDialogue: '',
    keyMoments: '',
    tone: '',
    pacing: '',
    avoidNotes: ''
  });

  // Competition deadline - Fixed date: May 21, 2026 at midnight UTC
  const COMPETITION_DEADLINE = new Date('2026-05-21T00:00:00Z');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('moviebot_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  // Countdown timer
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = COMPETITION_DEADLINE - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, []);

  // Check if project is already in challenge
  useEffect(() => {
    const checkChallengeStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/challenge/my-entries`, {
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const entries = await res.json();
          const isEntered = entries.some(e => e.project_id === projectId);
          setChallengeEntered(isEntered);
        }
      } catch (e) {
        // Ignore errors
      }
    };
    checkChallengeStatus();
  }, [projectId]);

  useEffect(() => {
    if (!selectedBeat && STORY_BEATS[1]) {
      setSelectedBeat({ ...STORY_BEATS[1].beats[0], act: 1 });
    }
  }, []);

  // Load AI options when beat changes or step changes
  useEffect(() => {
    if (selectedBeat && !viewingScene) {
      loadAIOptions();
    }
  }, [selectedBeat, currentStep]);

  const fetchProjectData = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [projectRes, scenesRes, charactersRes, accessRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/api/projects/${projectId}`, {  headers }),
        fetch(`${API_URL}/api/projects/${projectId}/scenes`, {  headers }),
        fetch(`${API_URL}/api/projects/${projectId}/characters`, {  headers }),
        fetch(`${API_URL}/api/projects/${projectId}/access-level`, {  headers }),
        fetch(`${API_URL}/api/user/daily-usage`, { headers })
      ]);

      if (!projectRes.ok) {
        navigate('/dashboard');
        return;
      }

      const [projectData, scenesData, charactersData, accessData] = await Promise.all([
        projectRes.json(),
        scenesRes.json(),
        charactersRes.json(),
        accessRes.json()
      ]);
      
      // Handle daily usage separately (may fail for different reasons)
      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setDailyUsage(usageData.scene_generations);
      }

      setProject(projectData);
      setScenes(scenesData);
      setCharacters(charactersData);
      setAccessLevel(accessData);
      
      if (STORY_BEATS[1]) {
        const firstBeat = { ...STORY_BEATS[1].beats[0], act: 1 };
        setSelectedBeat(firstBeat);
      }
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const canAccessAct = (actNum) => {
    // All acts are visible, access is controlled at beat level now
    return true;
  };

  const canAccessBeat = (beatId) => {
    if (!accessLevel) return FREE_TIER_BEATS.includes(beatId);
    if (accessLevel.access.unlimited_beats) return true;
    return accessLevel.access.allowed_beats.includes(beatId);
  };

  const loadAIOptions = async () => {
    if (!selectedBeat) return;
    
    setLoadingOptions(true);
    setAiOptions(null);
    
    const stepType = CHOICE_STEPS[currentStep].id;
    const previousChoices = Object.entries(userChoices)
      .filter(([k, v]) => v !== null)
      .map(([k, v]) => `${k}: ${v.description}`)
      .join('. ');
    
    // Build rich context including beat position and previous choices
    const context = `
Act ${selectedBeat.act} - Beat: ${selectedBeat.name}
Beat description: ${selectedBeat.description}
${previousChoices ? 'Choices made so far for this scene: ' + previousChoices : 'Starting fresh with this scene.'}
    `.trim();

    try {
      const response = await fetch(`${API_URL}/api/ai/choices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          project_id: projectId,
          choice_type: stepType,
          context: context
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiOptions(data);
      } else {
        toast.error('Failed to load options');
      }
    } catch (error) {
      toast.error('Failed to load AI options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const selectChoice = (choice) => {
    const stepType = CHOICE_STEPS[currentStep].id;
    setUserChoices(prev => ({ ...prev, [stepType]: choice }));
    setShowCustomInput(false);
    setCustomInput('');
    
    if (currentStep < CHOICE_STEPS.length - 1) {
      // Move to next step
      setCurrentStep(currentStep + 1);
    }
  };

  const selectCustomChoice = () => {
    if (!customInput.trim()) return;
    
    const stepType = CHOICE_STEPS[currentStep].id;
    const customChoice = {
      option: 'Custom',
      description: customInput.trim()
    };
    setUserChoices(prev => ({ ...prev, [stepType]: customChoice }));
    setShowCustomInput(false);
    setCustomInput('');
    
    if (currentStep < CHOICE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const resetChoices = () => {
    setUserChoices({ plot: null, dialogue: null, character_action: null });
    setCurrentStep(0);
    setGeneratedScene(null);
    setViewingScene(false);
    setShowFeedbackOptions(false);
    setCustomSuggestion('');
    setShowCustomInput(false);
    setCustomInput('');
  };

  const selectBeat = (beat, actNum) => {
    if (!canAccessBeat(beat.id)) {
      toast.error('Upgrade to access this beat');
      return;
    }
    
    // Check if beat already has scenes
    const beatScenes = scenes.filter(s => s.beat_id === beat.id && s.act === actNum);
    
    setSelectedBeat({ ...beat, act: actNum });
    setSelectedAct(actNum);
    resetChoices();
    
    if (beatScenes.length > 0) {
      // Show existing scene
      setGeneratedScene({
        heading: beatScenes[0].heading,
        content: beatScenes[0].action || ''
      });
      setViewingScene(true);
    }
  };

  const toggleAct = (actNum) => {
    setExpandedActs(prev => ({ ...prev, [actNum]: !prev[actNum] }));
  };

  const getBeatSceneCount = (beatId, actNum) => {
    return scenes.filter(s => s.beat_id === beatId && s.act === actNum).length;
  };

  const calculateProgress = () => {
    let totalBeats = 0;
    let completedBeats = 0;
    
    Object.entries(STORY_BEATS).forEach(([actNum, act]) => {
      act.beats.forEach(beat => {
        totalBeats++;
        if (getBeatSceneCount(beat.id, parseInt(actNum)) >= beat.suggested_scenes) completedBeats++;
      });
    });
    
    return totalBeats > 0 ? Math.round((completedBeats / totalBeats) * 100) : 0;
  };

  const allChoicesMade = userChoices.plot && userChoices.dialogue && userChoices.character_action;

  const generateScene = async () => {
    if (!allChoicesMade || !selectedBeat) return;
    
    setGeneratingScene(true);
    setShowFeedbackOptions(false);
    
    // Build context from user choices
    const choicesSummary = `
Plot: ${userChoices.plot.description}
Dialogue: ${userChoices.dialogue.description}
Character Actions: ${userChoices.character_action.description}
    `.trim();

    try {
      // First generate a scene heading
      const titleResponse = await fetch(`${API_URL}/api/projects/${projectId}/generate-scene-title`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          beat_id: selectedBeat.id,
          act: selectedBeat.act,
          context: choicesSummary
        })
      });
      
      let heading = `INT. ${selectedBeat.name.toUpperCase()} LOCATION - DAY`;
      if (titleResponse.ok) {
        const titleData = await titleResponse.json();
        heading = titleData.titles?.[0] || heading;
      }

      // Build choice details for the API
      const plotChoice = userChoices.plot?.description || '';
      const dialogueChoice = userChoices.dialogue?.description || '';
      const characterActionChoice = userChoices.character_action?.description || '';

      // Now develop the scene with user choices
      const response = await fetch(`${API_URL}/api/ai/develop-scene`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          project_id: projectId,
          heading: heading,
          description: `${selectedBeat.name}: ${selectedBeat.description}`,
          // Pass individual choices for better AI context
          plot_choice: plotChoice,
          dialogue_choice: dialogueChoice,
          character_action_choice: characterActionChoice,
          // Pass beat and act context
          current_beat: {
            id: selectedBeat.id,
            name: selectedBeat.name,
            description: selectedBeat.description
          },
          act_number: selectedBeat.act,
          // Pro Scene Studio inputs (only sent if Pro user has them filled)
          ...(showProStudio && user?.subscription_tier === 'pro' && {
            pro_studio: {
              scene_context: proStudioInputs.sceneContext || null,
              include_dialogue: proStudioInputs.includeDialogue || null,
              key_moments: proStudioInputs.keyMoments || null,
              tone: proStudioInputs.tone || null,
              pacing: proStudioInputs.pacing || null,
              avoid_notes: proStudioInputs.avoidNotes || null
            }
          })
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedScene({
          heading: heading.toUpperCase(),
          content: data.scene_content,
          saved: false // Not saved yet - waiting for user approval
        });
        setViewingScene(true);
        setShowFeedbackOptions(true);
        
        // Update daily usage from response
        if (data.usage) {
          setDailyUsage(data.usage);
        }
      } else if (response.status === 429) {
        // Daily limit reached
        const errorData = await response.json();
        setLimitInfo(errorData.detail);
        setShowLimitModal(true);
      } else {
        toast.error('Failed to generate scene');
      }
    } catch (error) {
      toast.error('Scene generation failed');
    } finally {
      setGeneratingScene(false);
    }
  };

  const acceptAndContinue = async () => {
    if (!generatedScene || !selectedBeat) return;
    
    try {
      // Save the scene to database
      const createResponse = await fetch(`${API_URL}/api/projects/${projectId}/scenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          act: selectedBeat.act,
          scene_number: scenes.filter(s => s.act === selectedBeat.act).length + 1,
          heading: generatedScene.heading,
          action: generatedScene.content,
          beat_id: selectedBeat.id
        })
      });

      if (createResponse.ok) {
        const newScene = await createResponse.json();
        const updatedScenes = [...scenes, newScene];
        setScenes(updatedScenes);
        setGeneratedScene({ ...generatedScene, saved: true });
        
        // Check if free user just completed their 2 free scenes (opening_image and theme_stated)
        const freeBeats = ["opening_image", "theme_stated"];
        const isFreeUser = !accessLevel?.access?.unlimited_beats;
        const completedFreeBeats = updatedScenes.filter(s => freeBeats.includes(s.beat_id)).length;
        
        if (isFreeUser && completedFreeBeats >= 2) {
          // Redirect to recap page first, then upgrade
          navigate(`/recap/${projectId}`);
          return;
        }
        
        toast.success('Scene saved! Moving to next beat...');
        
        // Find next beat
        const currentActBeats = STORY_BEATS[selectedBeat.act]?.beats || [];
        const currentBeatIndex = currentActBeats.findIndex(b => b.id === selectedBeat.id);
        
        if (currentBeatIndex < currentActBeats.length - 1) {
          // Next beat in same act
          const nextBeat = currentActBeats[currentBeatIndex + 1];
          if (canAccessBeat(nextBeat.id)) {
            selectBeat(nextBeat, selectedBeat.act);
          } else {
            // Next beat is locked - show recap then upgrade
            navigate(`/recap/${projectId}`);
          }
        } else if (selectedBeat.act < 3) {
          // First beat of next act
          const nextActBeats = STORY_BEATS[selectedBeat.act + 1]?.beats || [];
          if (nextActBeats.length > 0 && canAccessBeat(nextActBeats[0].id)) {
            selectBeat(nextActBeats[0], selectedBeat.act + 1);
            setExpandedActs(prev => ({ ...prev, [selectedBeat.act + 1]: true }));
          }
        } else {
          // Completed all beats
          toast.success('Congratulations! You\'ve completed your screenplay!');
          setShowFeedbackOptions(false);
        }
      }
    } catch (error) {
      toast.error('Failed to save scene');
    }
  };

  const rewriteWithSuggestions = async () => {
    if (!customSuggestion.trim() || !selectedBeat) {
      toast.error('Please enter your suggestions');
      return;
    }
    
    setRewritingScene(true);

    // Get the individual choices from userChoices state
    const plotChoice = userChoices.plot?.description || '';
    const dialogueChoice = userChoices.dialogue?.description || '';
    const characterActionChoice = userChoices.character_action?.description || '';

    try {
      const response = await fetch(`${API_URL}/api/ai/develop-scene`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          project_id: projectId,
          heading: generatedScene?.heading || `INT. ${selectedBeat.name.toUpperCase()} LOCATION - DAY`,
          description: `${selectedBeat.name}: ${selectedBeat.description}.

USER'S CUSTOM FEEDBACK FOR REWRITE:
${customSuggestion}

IMPORTANT: This is a REWRITE. Incorporate the user's custom suggestions above while maintaining story continuity.`,
          // Pass individual choices
          plot_choice: plotChoice,
          dialogue_choice: dialogueChoice,
          character_action_choice: characterActionChoice,
          // Pass beat and act context
          current_beat: {
            id: selectedBeat.id,
            name: selectedBeat.name,
            description: selectedBeat.description
          },
          act_number: selectedBeat.act
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedScene({
          heading: generatedScene?.heading || `INT. ${selectedBeat.name.toUpperCase()} LOCATION - DAY`,
          content: data.scene_content,
          saved: false
        });
        setCustomSuggestion('');
        toast.success('Scene rewritten with your suggestions!');
        
        // Update daily usage from response
        if (data.usage) {
          setDailyUsage(data.usage);
        }
      } else if (response.status === 429) {
        // Daily limit reached
        const errorData = await response.json();
        setLimitInfo(errorData.detail);
        setShowLimitModal(true);
      } else {
        toast.error('Failed to rewrite scene');
      }
    } catch (error) {
      toast.error('Rewrite failed');
    } finally {
      setRewritingScene(false);
    }
  };

  const enterChallenge = async () => {
    setEnteringChallenge(true);
    try {
      const res = await fetch(`${API_URL}/api/challenge/enter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          project_id: projectId,
          confirmations: {
            story_foundation_complete: true,
            following_moviebot: false,
            shared_challenge: false,
            original_work: true
          }
        })
      });
      
      if (res.ok) {
        setChallengeEntered(true);
        toast.success('Project entered in the Summer Competition!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to enter competition');
      }
    } catch (e) {
      toast.error('Failed to enter competition');
    } finally {
      setEnteringChallenge(false);
    }
  };

  const exportScript = async () => {
    if (!accessLevel?.access?.can_export) {
      toast.error('Upgrade to Basic to export your script');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/export`, {
        headers: getAuthHeaders(),
        
      });

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.title}.fountain`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Script exported');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const generateCoverageReport = async () => {
    if (!accessLevel?.access?.coverage_report) {
      toast.error('Script Coverage Report is a Pro feature');
      return;
    }

    setCoverageLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/coverage-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
      });

      if (response.ok) {
        const data = await response.json();
        setCoverageReport(data);
        setShowCoverageModal(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Coverage report generation failed');
    } finally {
      setCoverageLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col md:flex-row">
      {/* Mobile Header */}
      <MobileHeader
        project={project}
        setShowMobileLeftSidebar={setShowMobileLeftSidebar}
        setShowMobileRightSidebar={setShowMobileRightSidebar}
        calculateProgress={calculateProgress}
      />

      {/* Mobile Left Sidebar */}
      <MobileLeftSidebar
        showMobileLeftSidebar={showMobileLeftSidebar}
        setShowMobileLeftSidebar={setShowMobileLeftSidebar}
        expandedActs={expandedActs}
        toggleAct={toggleAct}
        selectedBeat={selectedBeat}
        selectBeat={selectBeat}
        canAccessBeat={canAccessBeat}
        getBeatSceneCount={getBeatSceneCount}
        exportScript={exportScript}
        generateCoverageReport={generateCoverageReport}
        coverageLoading={coverageLoading}
        accessLevel={accessLevel}
        challengeEntered={challengeEntered}
        enterChallenge={enterChallenge}
        enteringChallenge={enteringChallenge}
        timeLeft={timeLeft}
      />

      {/* Mobile Right Sidebar */}
      <MobileRightSidebar
        showMobileRightSidebar={showMobileRightSidebar}
        setShowMobileRightSidebar={setShowMobileRightSidebar}
        scenes={scenes}
        selectedBeat={selectedBeat}
        setSelectedBeat={setSelectedBeat}
        setGeneratedScene={setGeneratedScene}
        setViewingScene={setViewingScene}
        setShowFeedbackOptions={setShowFeedbackOptions}
        STORY_BEATS={STORY_BEATS}
      />

      {/* Desktop Left Sidebar - Story Structure */}
      <aside className="hidden md:flex w-72 bg-secondary/30 border-r border-white/5 flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/5">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-4" data-testid="editor-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <Film className="w-5 h-5 text-primary" />
            <span className="font-bold text-white truncate">{project?.title}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Progress</span>
            <span className="text-xs text-primary font-bold">{calculateProgress()}%</span>
          </div>
          <Progress value={calculateProgress()} className="h-1 mt-2" />
        </div>

        {/* Acts & Beats */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            {Object.entries(STORY_BEATS).map(([actNum, actData]) => {
              const actNumInt = parseInt(actNum);
              const isExpanded = expandedActs[actNumInt];
              
              return (
                <div key={actNum} className="mb-2">
                  <button
                    onClick={() => toggleAct(actNumInt)}
                    className={`w-full flex items-center justify-between p-3 transition-colors ${
                      isExpanded ? 'bg-primary/20 text-primary' : 'bg-white/5 text-white hover:bg-white/10'
                    }`}
                    data-testid={`act-${actNum}-header`}
                  >
                    <div className="flex items-center gap-2">
                      <Circle className="w-4 h-4" />
                      <span className="font-bold text-sm">Act {actNum}</span>
                    </div>
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <div className="py-1">
                          {actData.beats.map((beat) => {
                            const sceneCount = getBeatSceneCount(beat.id, actNumInt);
                            const isComplete = sceneCount >= beat.suggested_scenes;
                            const isSelected = selectedBeat?.id === beat.id && selectedBeat?.act === actNumInt;
                            const beatLocked = !canAccessBeat(beat.id);
                            
                            return (
                              <button
                                key={beat.id}
                                onClick={() => !beatLocked ? selectBeat(beat, actNumInt) : null}
                                className={`w-full flex items-center justify-between p-3 pl-6 transition-all ${
                                  beatLocked ? 'opacity-50 cursor-not-allowed' :
                                  isSelected ? 'bg-primary/30 border-l-2 border-primary' : 'hover:bg-white/5 border-l-2 border-transparent'
                                }`}
                                data-testid={`beat-${beat.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  {beatLocked ? (
                                    <Lock className="w-3 h-3 text-muted-foreground" />
                                  ) : isComplete ? (
                                    <CheckCircle className="w-3 h-3 text-green-400" />
                                  ) : (
                                    <Circle className="w-3 h-3 text-muted-foreground" />
                                  )}
                                  <span className={`text-xs ${beatLocked ? 'text-muted-foreground' : isSelected ? 'text-primary font-medium' : 'text-white'}`}>{beat.name}</span>
                                </div>
                                {beatLocked ? (
                                  <Link to="/subscription" onClick={(e) => e.stopPropagation()}>
                                    <span className="text-xs text-primary hover:underline flex items-center gap-1">
                                      <Crown className="w-3 h-3" /> Upgrade
                                    </span>
                                  </Link>
                                ) : (
                                  <span className={`text-xs ${isComplete ? 'text-green-400' : 'text-muted-foreground'}`}>{sceneCount}/{beat.suggested_scenes}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        {/* Export & Coverage */}
        <div className="p-4 border-t border-white/5 space-y-2">
          <Button onClick={exportScript} variant="outline" className={`w-full border-white/20 text-white text-xs ${!accessLevel?.access?.can_export ? 'opacity-50' : ''}`}>
            <Download className="w-4 h-4 mr-2" /> Export {!accessLevel?.access?.can_export && <Lock className="w-3 h-3 ml-2" />}
          </Button>
          <Button onClick={generateCoverageReport} disabled={coverageLoading} className={`w-full text-xs ${accessLevel?.access?.coverage_report ? 'bg-primary text-black' : 'bg-white/10 text-white/50'}`}>
            {coverageLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Coverage {!accessLevel?.access?.coverage_report && <Lock className="w-3 h-3 ml-2" />}
          </Button>
        </div>

        {/* Challenge Opt-In */}
        {!challengeEntered && (
          <div className="p-3 border-t border-teal-500/20 bg-gradient-to-r from-teal-500/10 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-4 h-4 text-teal-400" />
              <span className="text-xs font-bold text-teal-400">Summer Competition</span>
            </div>
            <p className="text-xs text-teal-200/70 mb-2">Enter your screenplay — we produce the winner!</p>
            <Button 
              onClick={enterChallenge}
              disabled={enteringChallenge}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white text-xs py-2"
              data-testid="sidebar-challenge-btn"
            >
              {enteringChallenge ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Entering...</>
              ) : (
                <>Enter Competition</>
              )}
            </Button>
            <span className="text-[10px] text-teal-300/50 flex items-center justify-center gap-1 mt-1">
              <Sparkles className="w-2.5 h-2.5" />
              Summer 2026
            </span>
          </div>
        )}
        {challengeEntered && (
          <div className="p-3 border-t border-green-500/20 bg-gradient-to-r from-green-500/10 to-transparent">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-xs font-bold text-green-400">Competition Entry Submitted</span>
            </div>
            <p className="text-xs text-green-200/70 mt-1">Good luck! Winners announced after deadline.</p>
          </div>
        )}
      </aside>

      {/* Center - AI Collaborative Assistant */}
      <main className="flex-1 flex flex-col pt-[88px] md:pt-0">
        {/* Beat Header */}
        {selectedBeat && (
          <div className="p-4 md:p-6 border-b border-white/5 bg-gradient-to-r from-primary/10 to-transparent">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <span>Act {selectedBeat.act}</span>
                  <ChevronRight className="w-3 h-3" />
                  <span className="text-primary">{selectedBeat.name}</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white">{selectedBeat.name}</h2>
                <p className="text-muted-foreground text-xs md:text-sm mt-1">{selectedBeat.description}</p>
              </div>
              {(generatedScene || viewingScene) && (
                <Button onClick={resetChoices} variant="outline" className="border-white/20 text-white text-xs w-full md:w-auto">
                  <RotateCcw className="w-4 h-4 mr-2" /> New Scene
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 overflow-auto">
          {viewingScene && generatedScene ? (
            /* View Generated Scene with Feedback Options */
            <div className="p-4 md:p-8">
              <div className="max-w-3xl mx-auto">
                {/* Scene Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-primary" /> 
                    {generatedScene.saved ? 'Saved Scene' : 'Generated Scene - Review'}
                  </h3>
                  {!showFeedbackOptions && (
                    <Button onClick={() => setViewingScene(false)} variant="ghost" className="text-primary text-xs">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back to Choices
                    </Button>
                  )}
                </div>

                {/* Scene Content */}
                <div className="screenplay-page mb-6">
                  <div className="scene-heading mb-4">{generatedScene.heading}</div>
                  <div className="whitespace-pre-wrap font-mono text-sm">{generatedScene.content}</div>
                </div>

                {/* Post-Generation Feedback Options */}
                {showFeedbackOptions && !generatedScene.saved && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4 md:space-y-6"
                  >
                    <div className="text-center p-3 md:p-4 bg-primary/10 border border-primary/30">
                      <h4 className="text-white font-bold mb-1 md:mb-2 text-sm md:text-base">What would you like to do?</h4>
                      <p className="text-xs md:text-sm text-muted-foreground">Review the scene above and choose how to proceed</p>
                    </div>

                    {/* Option 1: Accept and Continue */}
                    <button
                      onClick={acceptAndContinue}
                      className="w-full p-4 md:p-6 bg-green-500/10 border-2 border-green-500/30 hover:border-green-500 hover:bg-green-500/20 text-left transition-all group"
                      data-testid="accept-continue-btn"
                    >
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500/20 text-green-400 flex items-center justify-center flex-shrink-0 group-hover:bg-green-500 group-hover:text-white transition-colors">
                          <CheckCircle className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        <div>
                          <p className="text-green-400 font-bold mb-1 text-sm md:text-base">Use This Scene & Continue</p>
                          <p className="text-muted-foreground text-xs md:text-sm">Save this scene and move to the next story beat</p>
                        </div>
                      </div>
                    </button>

                    {/* Option 2: Custom Suggestions */}
                    <div className="p-4 md:p-6 bg-blue-500/10 border-2 border-blue-500/30">
                      <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        <div>
                          <p className="text-blue-400 font-bold mb-1 text-sm md:text-base">Make Custom Suggestions</p>
                          <p className="text-muted-foreground text-xs md:text-sm">Tell the AI how to improve this scene</p>
                        </div>
                      </div>
                      <Textarea
                        value={customSuggestion}
                        onChange={(e) => setCustomSuggestion(e.target.value)}
                        placeholder="e.g., Make the dialogue more tense, add a moment of hesitation..."
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 min-h-[80px] md:min-h-[100px] mb-3 md:mb-4 text-sm"
                        data-testid="custom-suggestion-input"
                      />
                      <Button
                        onClick={rewriteWithSuggestions}
                        disabled={!customSuggestion.trim() || rewritingScene}
                        className="w-full bg-blue-500 text-white hover:bg-blue-600 text-sm"
                        data-testid="rewrite-btn"
                      >
                        {rewritingScene ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rewriting...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" /> Rewrite Scene</>
                        )}
                      </Button>
                    </div>

                    {/* Option 3: Start Over */}
                    <button
                      onClick={resetChoices}
                      className="w-full p-4 md:p-6 bg-white/5 border-2 border-white/10 hover:border-white/30 hover:bg-white/10 text-left transition-all group"
                      data-testid="start-over-btn"
                    >
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 text-muted-foreground flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 group-hover:text-white transition-colors">
                          <RotateCcw className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        <div>
                          <p className="text-white font-bold mb-1 text-sm md:text-base">Start Over from Scratch</p>
                          <p className="text-muted-foreground text-xs md:text-sm">Go back to Plot Direction and make new choices</p>
                        </div>
                      </div>
                    </button>
                  </motion.div>
                )}

                {/* Scene Saved Confirmation */}
                {generatedScene.saved && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 text-center">
                    <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-bold">Scene Saved!</p>
                    <p className="text-muted-foreground text-sm mt-1">This scene has been added to your screenplay</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Collaborative Choice Flow */
            <div className="p-4 md:p-8">
              <div className="max-w-2xl mx-auto">
                {/* Progress Steps */}
                <div className="flex items-center justify-center mb-6 md:mb-10">
                  {CHOICE_STEPS.map((step, idx) => {
                    const StepIcon = step.icon;
                    const isActive = idx === currentStep;
                    const isComplete = userChoices[step.id] !== null;
                    
                    return (
                      <div key={step.id} className="flex items-center">
                        <button
                          onClick={() => idx <= currentStep && setCurrentStep(idx)}
                          disabled={idx > currentStep && !isComplete}
                          className={`flex flex-col items-center ${idx <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                          data-testid={`step-${step.id}`}
                        >
                          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
                            isComplete ? 'bg-green-500 text-white' :
                            isActive ? 'bg-primary text-black' :
                            'bg-white/10 text-muted-foreground'
                          }`}>
                            {isComplete ? <CheckCircle className="w-5 h-5 md:w-6 md:h-6" /> : <StepIcon className="w-5 h-5 md:w-6 md:h-6" />}
                          </div>
                          <span className={`text-[10px] md:text-xs mt-1 md:mt-2 hidden sm:block ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                            {step.name}
                          </span>
                        </button>
                        {idx < CHOICE_STEPS.length - 1 && (
                          <div className={`w-8 md:w-16 h-0.5 mx-1 md:mx-2 ${isComplete ? 'bg-green-500' : 'bg-white/10'}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Current Step Content */}
                <AnimatePresence mode="wait">
                  {!allChoicesMade ? (
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="text-center"
                    >
                      {/* Step Header */}
                      <div className="mb-6 md:mb-8">
                        <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/20 text-primary text-xs md:text-sm font-medium mb-3 md:mb-4">
                          <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
                          Step {currentStep + 1} of 3
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{CHOICE_STEPS[currentStep].name}</h3>
                        <p className="text-sm text-muted-foreground">{CHOICE_STEPS[currentStep].description}</p>
                      </div>

                      {/* Tip */}
                      <div className="mb-6 md:mb-8 p-3 md:p-4 bg-secondary/30 border border-white/10 text-left">
                        <div className="flex items-start gap-2 md:gap-3">
                          <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs md:text-sm text-white font-medium">Tip for {selectedBeat?.name}</p>
                            <p className="text-xs md:text-sm text-muted-foreground mt-1">{selectedBeat?.tips}</p>
                          </div>
                        </div>
                      </div>

                      {/* AI Options */}
                      {loadingOptions ? (
                        <div className="flex flex-col items-center py-12">
                          <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
                          <p className="text-muted-foreground">Generating options...</p>
                        </div>
                      ) : aiOptions?.choices ? (
                        <div className="space-y-3 md:space-y-4">
                          <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">Choose the direction that fits your vision:</p>
                          {aiOptions.choices.map((choice, idx) => (
                            <motion.button
                              key={idx}
                              initial={{ opacity: 0, x: idx === 0 ? -20 : 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              onClick={() => selectChoice(choice)}
                              className="w-full p-4 md:p-6 bg-secondary/30 border-2 border-white/10 hover:border-primary/50 hover:bg-primary/10 text-left transition-all group"
                              data-testid={`choice-${choice.option}`}
                            >
                              <div className="flex items-start gap-3 md:gap-4">
                                <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/20 text-primary font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-black transition-colors text-sm md:text-base">
                                  {choice.option}
                                </div>
                                <div>
                                  <p className="text-white font-medium mb-1 text-sm md:text-base">Option {choice.option}</p>
                                  <p className="text-muted-foreground text-xs md:text-sm">{choice.description}</p>
                                </div>
                              </div>
                            </motion.button>
                          ))}
                          
                          {/* Custom Input Option */}
                          <div className="relative mt-3 md:mt-4 pt-3 md:pt-4 border-t border-white/10">
                            <p className="text-xs text-muted-foreground text-center mb-2 md:mb-3">Or write your own direction</p>
                            {!showCustomInput ? (
                              <motion.button
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                onClick={() => setShowCustomInput(true)}
                                className="w-full p-4 md:p-6 bg-secondary/20 border-2 border-dashed border-white/20 hover:border-primary/50 hover:bg-primary/5 text-left transition-all group"
                                data-testid="custom-choice-btn"
                              >
                                <div className="flex items-start gap-3 md:gap-4">
                                  <div className="w-8 h-8 md:w-10 md:h-10 bg-white/10 text-white/60 font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:text-primary transition-colors">
                                    <Edit3 className="w-4 h-4 md:w-5 md:h-5" />
                                  </div>
                                  <div>
                                    <p className="text-white/80 font-medium mb-1 text-sm md:text-base">Write Your Own</p>
                                    <p className="text-muted-foreground text-xs md:text-sm">Have a different idea? Describe your own direction.</p>
                                  </div>
                                </div>
                              </motion.button>
                            ) : (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-secondary/30 border-2 border-primary/50 p-4 md:p-6"
                              >
                                <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                                  <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/20 text-primary font-bold flex items-center justify-center flex-shrink-0">
                                    <Edit3 className="w-4 h-4 md:w-5 md:h-5" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-medium mb-1 text-sm md:text-base">Your Custom Direction</p>
                                    <p className="text-muted-foreground text-xs mb-2 md:mb-3">Describe what you envision for this {CHOICE_STEPS[currentStep].name.toLowerCase()}</p>
                                    <textarea
                                      value={customInput}
                                      onChange={(e) => setCustomInput(e.target.value)}
                                      placeholder={`Example: ${CHOICE_STEPS[currentStep].id === 'plot' ? 'The protagonist discovers a hidden letter...' : CHOICE_STEPS[currentStep].id === 'dialogue' ? 'Short, tense exchanges...' : 'Nervous fidgeting, avoiding eye contact...'}`}
                                      className="w-full bg-black/50 border border-white/20 rounded-md p-3 text-white text-sm placeholder:text-white/30 focus:border-primary focus:outline-none resize-none"
                                      rows={3}
                                      data-testid="custom-input-textarea"
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setShowCustomInput(false); setCustomInput(''); }}
                                    className="text-muted-foreground hover:text-white text-xs md:text-sm"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={selectCustomChoice}
                                    disabled={!customInput.trim()}
                                    className="bg-primary text-black hover:bg-primary/90 text-xs md:text-sm"
                                    data-testid="use-custom-btn"
                                  >
                                    Use This Direction
                                  </Button>
                                </div>
                              </motion.div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="py-12 text-center">
                          <p className="text-muted-foreground">Failed to load options.</p>
                          <Button onClick={loadAIOptions} variant="outline" className="mt-4 text-primary border-primary">
                            <RotateCcw className="w-4 h-4 mr-2" /> Retry
                          </Button>
                        </div>
                      )}

                      {/* Previous Choices Summary */}
                      {currentStep > 0 && (
                        <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-white/10">
                          <p className="text-xs text-muted-foreground mb-2 md:mb-3">Your choices so far:</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {CHOICE_STEPS.slice(0, currentStep).map((step, idx) => {
                              const choice = userChoices[step.id];
                              if (!choice) return null;
                              return (
                                <div key={step.id} className={`px-2 md:px-3 py-1 text-xs rounded-full ${choice.option === 'Custom' ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'}`}>
                                  {step.name}: {choice.option === 'Custom' ? 'Custom' : `Option ${choice.option}`}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    /* All Choices Made - Generate Scene */
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-4 md:py-8"
                    >
                      <div className="mb-6 md:mb-8">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                          <PenTool className="w-8 h-8 md:w-10 md:h-10 text-primary" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Ready to Create Your Scene!</h3>
                        <p className="text-sm text-muted-foreground">Your creative choices are locked in. Let's bring them to life.</p>
                      </div>

                      {/* Choices Summary */}
                      <div className="bg-secondary/30 border border-white/10 p-4 md:p-6 mb-6 md:mb-8 text-left">
                        <h4 className="text-white font-bold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-400" /> Your Creative Choices
                        </h4>
                        <div className="space-y-2 md:space-y-3">
                          {CHOICE_STEPS.map((step) => {
                            const choice = userChoices[step.id];
                            const StepIcon = step.icon;
                            return (
                              <div key={step.id} className="flex items-start gap-2 md:gap-3">
                                <StepIcon className="w-3 h-3 md:w-4 md:h-4 text-primary mt-1 flex-shrink-0" />
                                <div>
                                  <p className="text-xs text-muted-foreground">{step.name}</p>
                                  <p className="text-xs md:text-sm text-white">{choice?.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Pro Scene Studio - Only for Pro users */}
                      {user?.subscription_tier === 'pro' && (
                        <div className="mb-6 md:mb-8 text-left">
                          <button
                            onClick={() => setShowProStudio(!showProStudio)}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-500/10 to-primary/10 border border-purple-500/30 rounded-lg hover:border-purple-500/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <Crown className="w-4 h-4 text-purple-400" />
                              </div>
                              <div className="text-left">
                                <span className="text-white font-bold text-sm">Pro Scene Studio</span>
                                <p className="text-xs text-purple-300">Add detailed context, dialogue & direction</p>
                              </div>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform ${showProStudio ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {showProStudio && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-3 space-y-4 p-4 bg-black/30 border border-purple-500/20 rounded-lg"
                            >
                              {/* Scene Context */}
                              <div>
                                <label className="block text-xs font-medium text-purple-300 mb-2">
                                  Scene Context & Notes
                                </label>
                                <textarea
                                  value={proStudioInputs.sceneContext}
                                  onChange={(e) => setProStudioInputs({...proStudioInputs, sceneContext: e.target.value})}
                                  placeholder="Add backstory, world-building details, or anything the AI should know about this scene..."
                                  className="w-full bg-black/50 border border-white/10 rounded p-3 text-sm text-white placeholder:text-muted-foreground/50 focus:border-purple-500/50 focus:outline-none min-h-[80px] resize-y"
                                />
                              </div>

                              {/* Include Dialogue */}
                              <div>
                                <label className="block text-xs font-medium text-purple-300 mb-2">
                                  Include This Dialogue
                                </label>
                                <textarea
                                  value={proStudioInputs.includeDialogue}
                                  onChange={(e) => setProStudioInputs({...proStudioInputs, includeDialogue: e.target.value})}
                                  placeholder="Specific lines or exchanges that MUST appear in the scene..."
                                  className="w-full bg-black/50 border border-white/10 rounded p-3 text-sm text-white placeholder:text-muted-foreground/50 focus:border-purple-500/50 focus:outline-none min-h-[60px] resize-y"
                                />
                              </div>

                              {/* Key Moments */}
                              <div>
                                <label className="block text-xs font-medium text-purple-300 mb-2">
                                  Key Moments to Include
                                </label>
                                <textarea
                                  value={proStudioInputs.keyMoments}
                                  onChange={(e) => setProStudioInputs({...proStudioInputs, keyMoments: e.target.value})}
                                  placeholder="e.g., 'Sarah discovers the letter', 'John finally confronts his father'..."
                                  className="w-full bg-black/50 border border-white/10 rounded p-3 text-sm text-white placeholder:text-muted-foreground/50 focus:border-purple-500/50 focus:outline-none min-h-[60px] resize-y"
                                />
                              </div>

                              {/* Tone & Pacing Row */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-purple-300 mb-2">
                                    Tone
                                  </label>
                                  <select
                                    value={proStudioInputs.tone}
                                    onChange={(e) => setProStudioInputs({...proStudioInputs, tone: e.target.value})}
                                    className="w-full bg-black/50 border border-white/10 rounded p-2.5 text-sm text-white focus:border-purple-500/50 focus:outline-none"
                                  >
                                    <option value="">Select tone...</option>
                                    <option value="tense">Tense / Suspenseful</option>
                                    <option value="dramatic">Dramatic / Emotional</option>
                                    <option value="comedic">Comedic / Light</option>
                                    <option value="romantic">Romantic</option>
                                    <option value="action">Action-packed</option>
                                    <option value="melancholic">Melancholic / Sad</option>
                                    <option value="mysterious">Mysterious</option>
                                    <option value="hopeful">Hopeful / Uplifting</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-purple-300 mb-2">
                                    Pacing
                                  </label>
                                  <select
                                    value={proStudioInputs.pacing}
                                    onChange={(e) => setProStudioInputs({...proStudioInputs, pacing: e.target.value})}
                                    className="w-full bg-black/50 border border-white/10 rounded p-2.5 text-sm text-white focus:border-purple-500/50 focus:outline-none"
                                  >
                                    <option value="">Select pacing...</option>
                                    <option value="slow">Slow Burn</option>
                                    <option value="moderate">Moderate / Balanced</option>
                                    <option value="building">Building Tension</option>
                                    <option value="fast">Fast-paced</option>
                                    <option value="rapid">Rapid-fire</option>
                                  </select>
                                </div>
                              </div>

                              {/* What to Avoid */}
                              <div>
                                <label className="block text-xs font-medium text-purple-300 mb-2">
                                  What to Avoid
                                </label>
                                <textarea
                                  value={proStudioInputs.avoidNotes}
                                  onChange={(e) => setProStudioInputs({...proStudioInputs, avoidNotes: e.target.value})}
                                  placeholder="e.g., 'Don't make the dialogue too on-the-nose', 'Avoid clichés'..."
                                  className="w-full bg-black/50 border border-white/10 rounded p-3 text-sm text-white placeholder:text-muted-foreground/50 focus:border-purple-500/50 focus:outline-none min-h-[60px] resize-y"
                                />
                              </div>

                              <div className="pt-2 border-t border-white/10">
                                <p className="text-xs text-muted-foreground flex items-center gap-2">
                                  <Sparkles className="w-3 h-3 text-purple-400" />
                                  All fields are optional. The AI will incorporate any context you provide.
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </div>
                      )}

                      {/* Generate Button */}
                      <Button
                        onClick={generateScene}
                        disabled={generatingScene}
                        className="bg-primary text-black hover:bg-primary/90 font-bold px-6 md:px-8 py-5 md:py-6 text-base md:text-lg w-full md:w-auto"
                        data-testid="generate-scene-btn"
                      >
                        {generatingScene ? (
                          <><Loader2 className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" /> Creating Scene...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 md:w-5 md:h-5 mr-2" /> Generate Scene</>
                        )}
                      </Button>

                      {/* Daily Usage Indicator */}
                      {!dailyUsage.unlimited && (
                        <div className="mt-4 text-center">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
                            dailyUsage.remaining <= 1 
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                              : 'bg-white/5 text-muted-foreground'
                          }`}>
                            <Clock className="w-3 h-3" />
                            {dailyUsage.remaining} of {dailyUsage.limit} generations remaining today
                            {dailyUsage.remaining <= 1 && (
                              <span className="text-amber-300">• Upgrade for unlimited</span>
                            )}
                          </div>
                        </div>
                      )}

                      <Button onClick={resetChoices} variant="ghost" className="mt-3 md:mt-4 text-muted-foreground text-xs md:text-sm">
                        <RotateCcw className="w-3 h-3 md:w-4 md:h-4 mr-2" /> Start Over
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Right Sidebar - All Scenes (Desktop only) */}
      <aside className="hidden md:flex w-80 bg-secondary/30 border-l border-white/5 flex-col">
        <div className="p-4 border-b border-white/5">
          <h3 className="text-white font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Your Scenes
          </h3>
          <p className="text-xs text-muted-foreground mt-1">{scenes.length} scene{scenes.length !== 1 ? 's' : ''} created</p>
        </div>

        <ScrollArea className="flex-1">
          {scenes.length > 0 ? (
            <div className="p-2 space-y-2">
              {/* Group scenes by act */}
              {[1, 2, 3].map(actNum => {
                const actScenes = scenes.filter(s => s.act === actNum);
                if (actScenes.length === 0) return null;
                
                return (
                  <div key={actNum} className="mb-4">
                    <div className="px-2 py-1 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                      Act {actNum}
                    </div>
                    {actScenes.map((scene, idx) => {
                      const beat = Object.values(STORY_BEATS[actNum]?.beats || []).find(b => b.id === scene.beat_id);
                      const isCurrentScene = viewingScene && generatedScene?.scene_id === scene.scene_id;
                      
                      return (
                        <button
                          key={scene.scene_id}
                          onClick={() => {
                            // Find the beat for this scene
                            const beatData = STORY_BEATS[scene.act]?.beats?.find(b => b.id === scene.beat_id);
                            if (beatData) {
                              setSelectedBeat({ ...beatData, act: scene.act });
                            }
                            setGeneratedScene({
                              scene_id: scene.scene_id,
                              heading: scene.heading,
                              content: scene.action,
                              saved: true
                            });
                            setViewingScene(true);
                            setShowFeedbackOptions(false);
                          }}
                          className={`w-full text-left p-3 rounded-md transition-all ${
                            isCurrentScene 
                              ? 'bg-primary/20 border border-primary/50' 
                              : 'bg-white/5 hover:bg-white/10 border border-transparent'
                          }`}
                          data-testid={`scene-${scene.scene_id}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                              isCurrentScene ? 'bg-primary text-black' : 'bg-white/10 text-muted-foreground'
                            }`}>
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium truncate ${isCurrentScene ? 'text-primary' : 'text-white'}`}>
                                {scene.heading || 'Untitled Scene'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {beat?.name || scene.beat_id}
                              </p>
                              <p className="text-xs text-muted-foreground/60 mt-1 line-clamp-2">
                                {scene.action?.substring(0, 80)}...
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 text-center">
              <Film className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-2">No scenes yet</p>
              <p className="text-xs text-muted-foreground/70">
                Select a beat from the left and make your creative choices to generate scenes
              </p>
            </div>
          )}
        </ScrollArea>

        {/* Current Selection Info */}
        {selectedBeat && (
          <div className="p-4 border-t border-white/5 bg-primary/5">
            <p className="text-xs text-muted-foreground mb-1">Currently working on:</p>
            <p className="text-sm text-primary font-medium">{selectedBeat.name}</p>
            <p className="text-xs text-muted-foreground">Act {selectedBeat.act}</p>
            
            {/* Choice Progress */}
            <div className="mt-3 space-y-1">
              {CHOICE_STEPS.map((step) => {
                const choice = userChoices[step.id];
                return (
                  <div key={step.id} className={`flex items-center gap-2 text-xs ${choice ? 'text-green-400' : 'text-muted-foreground/50'}`}>
                    {choice ? <CheckCircle className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                    {step.name}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </aside>

      {/* Coverage Report Modal */}
      <Dialog open={showCoverageModal} onOpenChange={setShowCoverageModal}>
        <DialogContent className="bg-secondary border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" /> Script Coverage Report
            </DialogTitle>
          </DialogHeader>
          
          {coverageReport && (
            <div className="space-y-6 text-white">
              <div className="text-center p-6 bg-black/30">
                <div className={`inline-block px-6 py-2 text-2xl font-black ${
                  coverageReport.report.overall_rating === 'RECOMMEND' ? 'bg-green-500/20 text-green-400' :
                  coverageReport.report.overall_rating === 'CONSIDER' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {coverageReport.report.overall_rating}
                </div>
                <p className="mt-4 text-muted-foreground">{coverageReport.report.executive_summary}</p>
              </div>

              {coverageReport.report.scores && (
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(coverageReport.report.scores).map(([key, value]) => (
                    <div key={key} className="p-4 bg-white/5 text-center">
                      <div className="text-2xl font-bold text-primary">{value}/10</div>
                      <div className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Daily Limit Reached Modal */}
      <Dialog open={showLimitModal} onOpenChange={setShowLimitModal}>
        <DialogContent className="bg-gradient-to-br from-secondary via-secondary to-amber-500/10 border-amber-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" /> Daily Limit Reached
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Visual indicator */}
            <div className="text-center p-6 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="text-5xl font-black text-amber-400 mb-2">
                {limitInfo?.used || dailyUsage.used}/{limitInfo?.limit || dailyUsage.limit}
              </div>
              <p className="text-amber-200">Scenes generated today</p>
            </div>
            
            <div className="text-center">
              <p className="text-white mb-2">
                {limitInfo?.tier === 'basic' 
                  ? "You've used all 5 of your daily scene generations."
                  : "You've reached your daily limit for scene generations."
                }
              </p>
              <p className="text-muted-foreground text-sm">
                {limitInfo?.tier === 'basic'
                  ? "Upgrade to Pro for unlimited scene generations and never hit a limit again!"
                  : "Upgrade your plan to generate more scenes and unlock your full creative potential!"
                }
              </p>
            </div>

            {/* Upgrade benefits */}
            <div className="bg-black/30 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-primary" />
                <span className="text-white font-bold">Pro Benefits:</span>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Unlimited scene generations per day</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>AI Script Coverage Reports</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Advanced dialogue analysis</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Priority support</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
                onClick={() => setShowLimitModal(false)}
              >
                Continue Tomorrow
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-primary to-amber-500 text-black font-bold"
                onClick={() => navigate(`/upgrade/${projectId}`)}
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Limits reset at midnight UTC
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScriptEditor;
